import React, { useState } from 'react';
import { 
  Activity, Users, AlertTriangle, TrendingDown, MessageCircle, TrendingUp, 
  Eye, Dumbbell, Heart, Stethoscope, FlaskConical, Search, Filter, ArrowRight, CheckCircle, Pill, Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ExercisePlanManagement } from './ExercisePlanManagement';
import TestPrescriptionManagement from './therapist/TestPrescriptionManagement';
import { PatientDetailsView } from './PatientDetailsView';
import { useTherapistDashboard } from '../lib/hooks/useDashboards';
import { useAuth } from '../lib/hooks/useAuth';
import { LoadingState } from './common/LoadingState';
import { AlertCard } from './common/AlertCard';
import { EmptyState } from './common/EmptyState';
import { useCreateTestSchedule } from '../lib/hooks/useTestSchedules';
import { TestType, TestFrequency } from '../lib/api/test-schedules';

interface TherapistDashboardProps {
  onNavigate?: (view: 'dashboard' | 'exercise' | 'vitals' | 'reports' | 'messaging' | 'appointments' | 'library' | 'settings' | 'notifications' | 'hometest' | 'nurse-vitals' | 'nurse-lab-sample') => void;
  onSelectPatient?: (patient: any) => void;
}

export function TherapistDashboard({ onNavigate, onSelectPatient }: TherapistDashboardProps) {
  // Get authenticated therapist
  const { user } = useAuth();
  
  // Fetch dashboard data from API
  const { data: dashboardData, isLoading, error, refetch } = useTherapistDashboard(true);
  
  const [showExercisePlan, setShowExercisePlan] = useState(false);
  const [exercisePlanPatient, setExercisePlanPatient] = useState<{ id: string; name: string } | null>(null);
  const [showTestPrescription, setShowTestPrescription] = useState(false);
  const [viewingPatient, setViewingPatient] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeSection, setActiveSection] = useState<'overview' | 'patients' | 'symptoms-alerts' | 'low-adherence'>('overview');
  
  // Test prescription for specific patient
  const [showTestPrescriptionDialog, setShowTestPrescriptionDialog] = useState(false);
  const [testPrescriptionPatient, setTestPrescriptionPatient] = useState<{ id: number; name: string; umr_no: string } | null>(null);
  const [testType, setTestType] = useState<TestType>('SPIROMETRY');
  const [frequency, setFrequency] = useState<TestFrequency>('WEEKLY');
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState('');
  const [instructions, setInstructions] = useState('');
  
  // Test prescription mutation
  const createTestMutation = useCreateTestSchedule();

  // Show loading state
  if (isLoading) {
    return <LoadingState message="Loading therapist dashboard..." />;
  }
  
  // Show error state
  if (error) {
    return (
      <div className="container mx-auto p-6">
        <AlertCard
          type="error"
          title="Failed to load dashboard"
          description={error?.message || "An error occurred while loading the dashboard"}
          action={
            <Button onClick={() => refetch()} variant="outline">
              Try Again
            </Button>
          }
        />
      </div>
    );
  }
  
  // Show empty state if no data
  if (!dashboardData) {
    return (
      <div className="container mx-auto p-6">
        <EmptyState 
          title="No dashboard data available" 
          description="Please contact support if this issue persists"
        />
      </div>
    );
  }

  // ✅ FIX: Check if user data is available
  if (!user) {
    return (
      <div className="container mx-auto p-6">
        <AlertCard
          type="warning"
          title="User information not available"
          description="Please try logging out and logging in again."
          action={
            <Button onClick={() => window.location.reload()} variant="outline">
              Refresh Page
            </Button>
          }
        />
      </div>
    );
  }

  // Use API data only - no mock fallbacks
  const stats = dashboardData?.stats || {
    total_assigned_patients: 0,
    active_exercise_plans: 0,
    today_sessions: 0,
    avg_completion_rate: 0,
    patients_needing_attention: 0,
  };

  const myPatients = dashboardData?.assigned_patients || [];

  // Low adherence patients - use API data
  const lowAdherencePatients = dashboardData?.patients_needing_attention || [];

  // Patients with symptoms - filter from API data
  const patientsWithSymptoms = myPatients.filter(patient => 
    patient.has_symptoms || patient.latest_symptom || patient.symptoms
  );

  // Patients with out of range vitals - filter from API data
  const patientsWithOutOfRangeVitals = myPatients.filter(patient => 
    patient.has_vital_alerts || patient.out_of_range_vital || patient.vital_alert
  );

  // Get patient adherence detail from API data
  const getPatientAdherenceDetail = (patient: any) => {
    // Use adherence_breakdown from API if available
    if (patient.adherence_breakdown) {
      return {
        medication: patient.adherence_breakdown.medication || { overall: 0, scheduledDoses: 0, takenDoses: 0, missedDoses: 0 },
        exercise: patient.adherence_breakdown.exercise || { overall: 0, assignedExercises: 0, completedExercises: 0 },
        homeTest: patient.adherence_breakdown.home_tests || { overall: 0, scheduledTests: 0, completedTests: 0 }
      };
    }
    
    // Fallback to basic structure if no breakdown available
    return {
      medication: { overall: patient.adherence || 0, scheduledDoses: 0, takenDoses: 0, missedDoses: 0 },
      exercise: { overall: patient.adherence || 0, assignedExercises: 0, completedExercises: 0 },
      homeTest: { overall: patient.adherence || 0, scheduledTests: 0, completedTests: 0 }
    };
  };

  // Filter patients
  const filteredPatients = myPatients.filter(patient => {
    const matchesSearch = patient.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || patient.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'On Track':
        return 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]';
      case 'Missed':
        return 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]';
      case 'Escalated':
        return 'bg-destructive/10 text-destructive border-destructive';
      default:
        return '';
    }
  };

  const handleCreateExercisePlan = (patientId: string, patientName: string) => {
    setExercisePlanPatient({ id: patientId, name: patientName });
    setShowExercisePlan(true);
  };

  const handleExercisePlanBack = () => {
    setShowExercisePlan(false);
    setExercisePlanPatient(null);
  };

  const handleSaveExercisePlan = (plan: any) => {
    console.log('Exercise plan saved:', plan);
    // Here you would typically save to backend/state management
  };

  const handleViewPatientDetails = (patient: any) => {
    setViewingPatient(patient);
  };

  const handleBackFromDetails = () => {
    setViewingPatient(null);
  };

  const handleEditExercisePlanFromDetails = (patientId: string, patientName: string, planId?: string) => {
    // Store the plan ID if editing existing plan
    setExercisePlanPatient({ id: patientId, name: patientName });
    setShowExercisePlan(true);
    // Note: planId can be used later if we want to edit specific plans
  };

  const handlePrescribeTestForPatient = (patient: any) => {
    setTestPrescriptionPatient({
      id: patient.id,
      name: patient.name,
      umr_no: patient.umr_no || patient.umrNo
    });
    // Reset form
    setTestType('SPIROMETRY');
    setFrequency('WEEKLY');
    setStartDate(format(new Date(), 'yyyy-MM-dd'));
    setEndDate('');
    setInstructions('');
    setShowTestPrescriptionDialog(true);
  };

  const handleCreateTestPrescription = async () => {
    if (!testPrescriptionPatient) {
      toast.error('No patient selected');
      return;
    }

    try {
      await createTestMutation.mutateAsync({
        patient_id: testPrescriptionPatient.id,
        test_type: testType,
        frequency: frequency,
        start_date: startDate,
        end_date: endDate || undefined,
        instructions: instructions || undefined,
      });

      // Close dialog and reset
      setShowTestPrescriptionDialog(false);
      setTestPrescriptionPatient(null);
      // Toast is already shown by the mutation hook
      
      // Refetch dashboard data
      refetch();
    } catch (error: any) {
      // Error toast is already shown by the mutation hook
      console.error('Test prescription failed:', error);
    }
  };

  // Show Exercise Plan Creation view
  if (showExercisePlan && exercisePlanPatient) {
    return (
      <ExercisePlanManagement
        patientId={exercisePlanPatient.id}
        patientName={exercisePlanPatient.name}
        onBack={handleExercisePlanBack}
        onSave={handleSaveExercisePlan}
      />
    );
  }

  // Show Patient Details view
  if (viewingPatient) {
    return (
      <PatientDetailsView
        patient={viewingPatient}
        cohortName={viewingPatient.cohort_name || viewingPatient.diagnosis || 'Unknown Cohort'}
        onBack={handleBackFromDetails}
        onEditExercisePlan={handleEditExercisePlanFromDetails}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 className="truncate">Welcome, {user?.full_name || user?.email || 'Therapist'}</h1>
          <p className="text-muted-foreground text-sm sm:text-base truncate">
            {dashboardData?.therapist_info?.specialization || 'Pulmonary Rehabilitation'}
          </p>
        </div>

        {/* Top Menu Navigation */}
        <div className="mb-4 sm:mb-6 flex flex-wrap gap-2">
          <Button
            variant={activeSection === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveSection('overview')}
            className={activeSection === 'overview' ? 'bg-primary' : ''}
            size="sm"
          >
            <Activity className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">Overview</span>
          </Button>
          <Button
            variant={activeSection === 'patients' ? 'default' : 'outline'}
            onClick={() => setActiveSection('patients')}
            className={activeSection === 'patients' ? 'bg-primary' : ''}
            size="sm"
          >
            <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">All Patients</span>
          </Button>
          <Button
            variant={activeSection === 'symptoms-alerts' ? 'default' : 'outline'}
            onClick={() => setActiveSection('symptoms-alerts')}
            className={activeSection === 'symptoms-alerts' ? 'bg-primary' : ''}
            size="sm"
          >
            <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">Symptoms & Alerts</span>
          </Button>
          <Button
            variant={activeSection === 'low-adherence' ? 'default' : 'outline'}
            onClick={() => setActiveSection('low-adherence')}
            className={activeSection === 'low-adherence' ? 'bg-primary' : ''}
            size="sm"
          >
            <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">Low Adherence</span>
          </Button>
          <Button
            onClick={() => onNavigate?.('messaging')}
            className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-white"
            size="sm"
          >
            <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">Chat</span>
          </Button>
          <Button
            onClick={() => setShowTestPrescription(true)}
            className="bg-[#F07D13] hover:bg-[#F07D13]/90 text-white"
            size="sm"
          >
            <FlaskConical className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">Prescribe Tests</span>
          </Button>
        </div>
        
        {/* Test Prescription Management Modal/View */}
        {showTestPrescription && (
          <div className="fixed inset-0 z-50 bg-white">
            <TestPrescriptionManagement onBack={() => setShowTestPrescription(false)} />
          </div>
        )}

        {/* Overview Section */}
        {activeSection === 'overview' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
              <Card 
                className="shadow-lg cursor-pointer hover:bg-secondary/50 transition-colors"
                onClick={() => setActiveSection('patients')}
              >
                <CardContent className="p-4 sm:p-5 md:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs sm:text-sm truncate">Total Patients</p>
                      <h2 className="text-primary text-xl sm:text-2xl">{myPatients.length}</h2>
                      <p className="text-muted-foreground text-xs mt-1">Click to view all</p>
                    </div>
                    <Users className="w-6 h-6 sm:w-8 sm:h-8 text-primary flex-shrink-0 ml-2" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Alert Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
              <Card 
                className="shadow-lg border-l-4 border-l-destructive cursor-pointer hover:bg-secondary/50 transition-colors"
                onClick={() => setActiveSection('symptoms-alerts')}
              >
                <CardContent className="p-4 sm:p-5 md:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs sm:text-sm truncate">Patients with Symptoms</p>
                      <h2 className="text-destructive text-xl sm:text-2xl">{patientsWithSymptoms.length}</h2>
                      <p className="text-muted-foreground text-xs mt-1">Click to view details</p>
                    </div>
                    <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-destructive flex-shrink-0 ml-2" />
                  </div>
                </CardContent>
              </Card>

              <Card 
                className="shadow-lg border-l-4 border-l-accent cursor-pointer hover:bg-secondary/50 transition-colors"
                onClick={() => setActiveSection('symptoms-alerts')}
              >
                <CardContent className="p-4 sm:p-5 md:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs sm:text-sm truncate">Alerts</p>
                      <h2 className="text-accent text-xl sm:text-2xl">{patientsWithOutOfRangeVitals.length}</h2>
                      <p className="text-muted-foreground text-xs mt-1">Click to view details</p>
                    </div>
                    <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-accent flex-shrink-0 ml-2" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Patients - Quick Access */}
            <Card className="shadow-lg">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-primary text-lg sm:text-xl">Recent Patients</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                <div className="grid grid-cols-1 gap-3 sm:gap-4">
                  {myPatients.slice(0, 5).map((patient) => {
                    return (
                      <div key={patient.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 border border-border rounded-xl hover:bg-secondary/50 transition-colors">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                            <span className="text-primary-foreground text-sm sm:text-base">
                              {patient.name.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm sm:text-base">{patient.name}</p>
                            <p className="text-muted-foreground text-xs sm:text-sm truncate">
                              {patient.cohort_name || patient.diagnosis || 'No cohort assigned'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                          <Badge variant="outline" className={`${getStatusColor(patient.status)} text-xs`}>
                            {patient.status}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewPatientDetails(patient)}
                            className="text-xs sm:text-sm"
                          >
                            <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            <span className="hidden sm:inline">View Details</span>
                            <span className="sm:hidden">View</span>
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* All Patients Section */}
        {activeSection === 'patients' && (
          <>
            {/* Search and Filters */}
            <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground" />
                <Input
                  placeholder="Search patients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 sm:pl-10 text-sm sm:text-base h-9 sm:h-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40 md:w-48 h-9 sm:h-10 text-sm sm:text-base">
                  <Filter className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="On Track">On Track</SelectItem>
                  <SelectItem value="Missed">Missed</SelectItem>
                  <SelectItem value="Escalated">Escalated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Patient List */}
            <div className="grid grid-cols-1 gap-3 sm:gap-4">
              {filteredPatients.length === 0 ? (
                <Card className="shadow-lg">
                  <CardContent className="p-8 sm:p-12 text-center">
                    <Users className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-primary mb-2">No Patients Found</h3>
                    <p className="text-muted-foreground text-sm sm:text-base">
                      {searchQuery || statusFilter !== 'all' 
                        ? 'Try adjusting your search or filter criteria' 
                        : 'No patients are currently assigned to you'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                filteredPatients.map((patient) => {
                  return (
                    <Card key={patient.id} className="shadow-lg">
                      <CardContent className="p-3 sm:p-4 md:p-6">
                        <div className="flex flex-col gap-3 sm:gap-4">
                          {/* Patient Info Row */}
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                              <span className="text-primary-foreground text-sm sm:text-base">
                                {patient.name.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm sm:text-base">{patient.name}</p>
                              <p className="text-muted-foreground text-xs sm:text-sm truncate">
                                {patient.cohort_name || patient.diagnosis || 'No cohort assigned'}
                              </p>
                            </div>
                          </div>

                          {/* Stats and Actions Row */}
                          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                            {/* Stats */}
                            <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground text-xs">Streak:</span>
                                <div className="flex items-center gap-1">
                                  <div className="w-12 sm:w-16 bg-secondary rounded-full h-2">
                                    <div
                                      className="bg-accent h-2 rounded-full"
                                      style={{ width: `${Math.min((patient.streak / 30) * 100, 100)}%` }}
                                    />
                                  </div>
                                  <span className="text-accent text-xs sm:text-sm">{patient.streak}d</span>
                                </div>
                              </div>

                              <div className="flex items-center gap-1">
                                <span className="text-muted-foreground text-xs">Adherence:</span>
                                <span className="text-primary text-xs sm:text-sm">{patient.adherence}%</span>
                              </div>

                              <Badge variant="outline" className={`${getStatusColor(patient.status)} text-xs whitespace-nowrap`}>
                                {patient.status}
                              </Badge>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-1.5 sm:gap-2 flex-wrap sm:ml-auto">
                              <Button
                                size="sm"
                                onClick={() => handleViewPatientDetails(patient)}
                                className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-xs sm:text-sm px-2 sm:px-3 h-8"
                              >
                                <Eye className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">View</span>
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleCreateExercisePlan(patient.id, patient.name)}
                                className="bg-primary hover:bg-primary/90 text-xs sm:text-sm px-2 sm:px-3 h-8"
                              >
                                <Dumbbell className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">Plan</span>
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => {
                                  if (onSelectPatient && onNavigate) {
                                    onSelectPatient(patient);
                                    onNavigate('nurse-vitals');
                                    toast.info(`Collecting vitals for ${patient.name}`);
                                  }
                                }}
                                className="bg-[#494949] hover:bg-[#494949]/90 text-white text-xs sm:text-sm px-2 sm:px-3 h-8"
                              >
                                <Heart className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">Vitals</span>
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handlePrescribeTestForPatient(patient)}
                                className="bg-[#F07D13] hover:bg-[#F07D13]/90 text-white text-xs sm:text-sm px-2 sm:px-3 h-8"
                                title="Prescribe Home Test"
                              >
                                <FlaskConical className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">Prescribe</span>
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </>
        )}

        {/* Symptoms and Alerts Section */}
        {activeSection === 'symptoms-alerts' && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6 mb-6">
              <Card className="shadow-lg border-l-4 border-l-destructive">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-destructive" />
                      <h3 className="text-destructive">Patients with Symptoms</h3>
                    </div>
                    <Badge variant="destructive" className="text-xs sm:text-sm">
                      {patientsWithSymptoms.length}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Patients reporting concerning symptoms requiring attention
                  </p>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-l-4 border-l-accent">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-accent" />
                      <h3 className="text-accent">Alerts</h3>
                    </div>
                    <Badge className="bg-accent text-xs sm:text-sm">
                      {patientsWithOutOfRangeVitals.length}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Patients with vitals outside normal parameters
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Patients with Symptoms */}
            <Card className="shadow-lg mb-4 sm:mb-6">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-destructive text-base sm:text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Patients with Symptoms
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                {patientsWithSymptoms.length === 0 ? (
                  <div className="text-center py-6 sm:py-8">
                    <Activity className="w-10 h-10 sm:w-12 sm:h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm sm:text-base">
                      No patients with reported symptoms
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:gap-4">
                    {patientsWithSymptoms.map((patient) => {
                      // Use actual symptom data from API
                      const symptom = patient.latest_symptom || patient.symptoms || 'Reported symptoms';
                      
                      return (
                        <div key={patient.id} className="p-3 sm:p-4 border border-destructive/30 rounded-xl bg-destructive/5">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-destructive flex items-center justify-center flex-shrink-0">
                                <span className="text-destructive-foreground text-sm sm:text-base">
                                  {patient.name.split(' ').map(n => n[0]).join('')}
                                </span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm sm:text-base">{patient.name}</p>
                                <p className="text-muted-foreground text-xs sm:text-sm truncate">
                                  {patient.cohort_name || patient.diagnosis || 'No cohort assigned'}
                                </p>
                                <div className="flex items-center gap-1 mt-1">
                                  <AlertTriangle className="w-3 h-3 text-destructive" />
                                  <p className="text-destructive text-xs">{symptom}</p>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                              <Button
                                size="sm"
                                onClick={() => handleViewPatientDetails(patient)}
                                className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-xs sm:text-sm px-2 sm:px-3 h-8"
                              >
                                <Eye className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">View</span>
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Patients with Alerts (Out of Range Vitals) */}
            <Card className="shadow-lg">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-accent text-base sm:text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Alerts - Out of Range Vitals
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                {patientsWithOutOfRangeVitals.length === 0 ? (
                  <div className="text-center py-6 sm:py-8">
                    <Heart className="w-10 h-10 sm:w-12 sm:h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm sm:text-base">
                      All patient vitals are within normal range
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:gap-4">
                    {patientsWithOutOfRangeVitals.map((patient) => {
                      // Use actual vitals data from API
                      const vitalIssue = patient.out_of_range_vital || patient.vital_alert || {
                        vital: 'Vital Alert',
                        value: 'N/A',
                        normal: 'See details'
                      };
                      
                      return (
                        <div key={patient.id} className="p-3 sm:p-4 border border-accent/30 rounded-xl bg-accent/5">
                          <div className="flex flex-col gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                                <span className="text-accent-foreground text-sm sm:text-base">
                                  {patient.name.split(' ').map(n => n[0]).join('')}
                                </span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm sm:text-base">{patient.name}</p>
                                <p className="text-muted-foreground text-xs sm:text-sm truncate">
                                  {patient.cohort_name || patient.diagnosis || 'No cohort assigned'}
                                </p>
                              </div>
                            </div>
                            
                            <div className="bg-white p-2 sm:p-3 rounded-lg">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-accent text-xs sm:text-sm">{vitalIssue.vital}</p>
                                  <p className="text-muted-foreground text-xs">Normal: {vitalIssue.normal}</p>
                                </div>
                                <p className="text-destructive">{vitalIssue.value}</p>
                              </div>
                            </div>

                            <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                              <Button
                                size="sm"
                                onClick={() => handleViewPatientDetails(patient)}
                                className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-xs sm:text-sm px-2 sm:px-3 h-8"
                              >
                                <Eye className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">View</span>
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Low Adherence Section */}
        {activeSection === 'low-adherence' && (
          <div className="space-y-6">
            <Card className="shadow-lg border-l-4 border-l-[#F07D13]">
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <CardTitle className="text-[#F07D13] flex items-center gap-2">
                      <TrendingDown className="w-5 h-5" />
                      Low Adherence Overview ({lowAdherencePatients.length} Patients)
                    </CardTitle>
                    {lowAdherencePatients.length > 0 && (
                      <Badge variant="outline" className="bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]">
                        {lowAdherencePatients.length} Need Review
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground">
                    Patients with adherence below 100%. Monitor their progress and provide support as needed.
                  </p>
                </div>
              </CardHeader>
              <CardContent>
                {lowAdherencePatients.length > 0 ? (
                  <div className="space-y-4">
                    {lowAdherencePatients
                      .sort((a, b) => a.adherence - b.adherence)
                      .map((patient) => {
                        const adherenceDetail = getPatientAdherenceDetail(patient);
                        return (
                          <Card key={patient.id} className="border-2 border-[#F07D13]/30">
                            <CardContent className="p-4 sm:p-6">
                              {/* Patient Header */}
                              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
                                <div className="flex items-start gap-3 sm:gap-4 flex-1">
                                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                                    <span className="text-primary-foreground">
                                      {patient.name.split(' ').map(n => n[0]).join('')}
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h3 className="text-primary mb-1">{patient.name}</h3>
                                    <p className="text-muted-foreground mb-2">
                                      {patient.age} years • {patient.cohort_name || patient.diagnosis || 'No cohort assigned'}
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                      <Badge variant="outline" className={getStatusColor(patient.status)}>
                                        {patient.status}
                                      </Badge>
                                      <Badge variant="outline" className="bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]">
                                        {patient.adherence}% Overall
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Adherence Breakdown */}
                              <div className="mb-4">
                                <h4 className="text-primary mb-3">Adherence Breakdown</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                  {/* Medication Adherence */}
                                  <div className={`p-4 rounded-xl border-2 ${
                                    adherenceDetail.medication.overall < 85 
                                      ? 'bg-[#FEEBDA] border-[#F07D13]/30' 
                                      : 'bg-[#D4F0F8] border-[#14CAF4]/30'
                                  }`}>
                                    <div className="flex items-center justify-between mb-2">
                                      <Pill className={`w-5 h-5 ${
                                        adherenceDetail.medication.overall < 85 ? 'text-[#F07D13]' : 'text-[#14CAF4]'
                                      }`} />
                                      <span className={`${
                                        adherenceDetail.medication.overall < 85 ? 'text-[#F07D13]' : 'text-[#14CAF4]'
                                      }`}>
                                        {adherenceDetail.medication.overall}%
                                      </span>
                                    </div>
                                    <p className="text-sm text-foreground">Medication</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {adherenceDetail.medication.takenDoses}/{adherenceDetail.medication.scheduledDoses} doses
                                    </p>
                                  </div>

                                  {/* Exercise Adherence */}
                                  <div className={`p-4 rounded-xl border-2 ${
                                    adherenceDetail.exercise.overall < 85 
                                      ? 'bg-[#FEEBDA] border-[#F07D13]/30' 
                                      : 'bg-[#D4F0F8] border-[#14CAF4]/30'
                                  }`}>
                                    <div className="flex items-center justify-between mb-2">
                                      <Activity className={`w-5 h-5 ${
                                        adherenceDetail.exercise.overall < 85 ? 'text-[#F07D13]' : 'text-[#14CAF4]'
                                      }`} />
                                      <span className={`${
                                        adherenceDetail.exercise.overall < 85 ? 'text-[#F07D13]' : 'text-[#14CAF4]'
                                      }`}>
                                        {adherenceDetail.exercise.overall}%
                                      </span>
                                    </div>
                                    <p className="text-sm text-foreground">Exercise</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {adherenceDetail.exercise.completedExercises}/{adherenceDetail.exercise.assignedExercises} sessions
                                    </p>
                                  </div>

                                  {/* Home Test Adherence */}
                                  <div className={`p-4 rounded-xl border-2 ${
                                    adherenceDetail.homeTest.overall < 85 
                                      ? 'bg-[#FEEBDA] border-[#F07D13]/30' 
                                      : 'bg-[#D4F0F8] border-[#14CAF4]/30'
                                  }`}>
                                    <div className="flex items-center justify-between mb-2">
                                      <FlaskConical className={`w-5 h-5 ${
                                        adherenceDetail.homeTest.overall < 85 ? 'text-[#F07D13]' : 'text-[#14CAF4]'
                                      }`} />
                                      <span className={`${
                                        adherenceDetail.homeTest.overall < 85 ? 'text-[#F07D13]' : 'text-[#14CAF4]'
                                      }`}>
                                        {adherenceDetail.homeTest.overall}%
                                      </span>
                                    </div>
                                    <p className="text-sm text-foreground">Home Tests</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {adherenceDetail.homeTest.completedTests}/{adherenceDetail.homeTest.scheduledTests} tests
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-[#14CAF4] mx-auto mb-4" />
                    <h3 className="text-primary mb-2">Excellent Adherence!</h3>
                    <p className="text-muted-foreground">
                      All patients are maintaining 100% adherence
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      
      {/* Test Prescription Dialog */}
      <Dialog open={showTestPrescriptionDialog} onOpenChange={setShowTestPrescriptionDialog}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#494949]">Prescribe Home Test</DialogTitle>
            <DialogDescription>
              Prescribe a home test for {testPrescriptionPatient?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Patient Info Display */}
            <div className="p-3 bg-[#2762AC]/5 rounded-xl border border-[#2762AC]/20">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-foreground text-xs">
                    {testPrescriptionPatient?.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <p className="text-sm">{testPrescriptionPatient?.name}</p>
                  <p className="text-xs text-muted-foreground">UMR: {testPrescriptionPatient?.umr_no}</p>
                </div>
              </div>
            </div>

            {/* Test Type */}
            <div>
              <Label htmlFor="testType" className="text-[#494949] mb-2 block">Test Type *</Label>
              <Select value={testType} onValueChange={(v) => setTestType(v as TestType)}>
                <SelectTrigger id="testType" className="rounded-xl border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SPIROMETRY">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-[#2762AC]" />
                      <span>Spirometry Test</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="SIX_MWT">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-[#14CAF4]" />
                      <span>6-Minute Walk Test</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Frequency */}
            <div>
              <Label htmlFor="frequency" className="text-[#494949] mb-2 block">Frequency *</Label>
              <Select value={frequency} onValueChange={(v) => setFrequency(v as TestFrequency)}>
                <SelectTrigger id="frequency" className="rounded-xl border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ONCE">One Time</SelectItem>
                  <SelectItem value="DAILY">Daily</SelectItem>
                  <SelectItem value="WEEKLY">Weekly</SelectItem>
                  <SelectItem value="BIWEEKLY">Bi-Weekly</SelectItem>
                  <SelectItem value="MONTHLY">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div>
              <Label htmlFor="startDate" className="text-[#494949] mb-2 block">Start Date *</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="rounded-xl border-gray-200"
              />
            </div>

            {/* End Date */}
            <div>
              <Label htmlFor="endDate" className="text-[#494949] mb-2 block">End Date (Optional)</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                className="rounded-xl border-gray-200"
              />
            </div>

            {/* Instructions */}
            <div>
              <Label htmlFor="instructions" className="text-[#494949] mb-2 block">Instructions (Optional)</Label>
              <Textarea
                id="instructions"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Enter any special instructions for the patient..."
                rows={3}
                className="rounded-xl border-gray-200 resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowTestPrescriptionDialog(false);
                setTestPrescriptionPatient(null);
              }}
              className="rounded-xl border-gray-200"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTestPrescription} 
              disabled={createTestMutation.isPending}
              className="bg-[#2762AC] hover:bg-[#1d4a7f] text-white rounded-xl"
            >
              {createTestMutation.isPending ? 'Prescribing...' : 'Prescribe Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}